import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useFollow from '../../hooks/useFollow';
import axiosInstance from '../../services/axios';
import { FaThumbsUp, FaSpotify, FaApple, FaInstagram, FaTwitter, FaFacebook, FaYoutube, FaTiktok, FaCog } from 'react-icons/fa';
import {
    Box,
    Image,
    Heading,
    Text,
    Divider,
    Flex,
    Spinner,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import { Link, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const Artist = () => {
    const { artistId } = useParams();
    const [artistInfo, setArtistInfo] = useState(null);
    const [catalogue, setCatalogue] = useState([]);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, user } = useAuth();
    const { followers, isFollowing, followArtist, unfollowArtist, loading: followLoading } = useFollow(artistId);
    
    // Check if this is the user's own profile
    const isOwnProfile = isAuthenticated && user && artistInfo && (user.user_id === artistInfo.user_id || user.user_id === artistId);

    useEffect(() => {
        // Prevent API calls with undefined artistId
        if (!artistId) {
            console.error('Artist.js: artistId is undefined');
            return;
        }

        const fetchArtistInfo = async () => {
            try {
                const response = await axiosInstance.get(
                    `/users/artist/profile?user_id=${artistId}`
                );
                setArtistInfo(response.data);
            } catch (error) {
                console.error('Error fetching artist info:', error);
            }
        };

        const fetchCatalogue = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/users/catalogue/user/${artistId}`);
                setCatalogue(response.data.catalogue);
            } catch (error) {
                console.error('Error fetching artist catalogue:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchArtistInfo();
        fetchCatalogue();
    }, [artistId]);

    if (!artistInfo) {
        return <Spinner />;
    }

    return (
        <Box
            w="100%"
            maxW="1400px"
            margin="auto"
            mx={{ base: 3, md: 6 }}
            mt={{ base: 6, md: 8 }}
        >
            {/* Two Column Layout for larger screens */}
            <Flex 
                direction={{ base: 'column', xl: 'row' }} 
                gap={{ base: 6, md: 8 }}
                mb={{ base: 6, md: 8 }}
            >
                {/* Left Column - Artist Info */}
                <Box flex={{ base: '1', xl: '1.2' }}>
                    <Box
                        bg="secondary.500"
                        borderRadius="2xl"
                        overflow="hidden"
                        boxShadow="2xl"
                    >
                        <Box p={{ base: 6, md: 8, lg: 10 }}>
                            <Flex 
                                direction={{ base: 'column', lg: 'row' }} 
                                gap={{ base: 8, md: 10, lg: 12 }}
                                align={{ base: "center", lg: "flex-start" }}
                            >
                                {/* Artist Image */}
                                <Box 
                                    flex="none"
                                    position="relative"
                                >
                                    <Box
                                        w={{ base: "260px", sm: "280px", md: "300px", lg: "320px" }}
                                        h={{ base: "260px", sm: "280px", md: "300px", lg: "320px" }}
                                        borderRadius="2xl"
                                        overflow="hidden"
                                        boxShadow="2xl"
                                        bg="dark.100"
                                    >
                                        <Image
                                            src={artistInfo.image_url}
                                            alt={artistInfo.artist_name}
                                            width="100%"
                                            height="100%"
                                            objectFit="cover"
                                            transition="transform 0.3s ease"
                                            _hover={{ transform: "scale(1.02)" }}
                                        />
                                    </Box>
                                </Box>

                                {/* Artist Info */}
                                <Box 
                                    flex="1" 
                                    w={{ base: "100%", lg: "auto" }}
                                    minW={0}
                                    textAlign={{ base: "center", lg: "left" }}
                                >
                                    {/* Artist Name with Settings Button */}
                                    <Flex 
                                        align="center" 
                                        justify={{ base: "center", lg: "flex-start" }}
                                        gap={4}
                                        mb={{ base: 4, md: 6 }}
                                        flexWrap="wrap"
                                    >
                                        <Heading 
                                            as="h1" 
                                            fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
                                            color="dark.900" 
                                            fontWeight="black"
                                            lineHeight="0.9"
                                            letterSpacing="-0.02em"
                                        >
                                            {artistInfo.artist_name}
                                        </Heading>
                                        
                                        {/* Settings Button for Own Profile */}
                                        {isOwnProfile && (
                                            <Tooltip label="Edit Profile Settings" hasArrow>
                                                <IconButton
                                                    as={RouterLink}
                                                    to="/dashboard/artist/profile"
                                                    icon={<FaCog />}
                                                    size="lg"
                                                    variant="ghost"
                                                    color="primary.500"
                                                    borderRadius="full"
                                                    _hover={{
                                                        bg: "dark.100",
                                                        color: "dark.900",
                                                        transform: "scale(1.05)"
                                                    }}
                                                    transition="all 0.2s ease"
                                                    aria-label="Edit profile settings"
                                                />
                                            </Tooltip>
                                        )}
                                    </Flex>

                                    {/* Stats and Actions */}
                                    <Flex 
                                        align="center" 
                                        justify={{ base: "center", lg: "flex-start" }}
                                        direction={{ base: "column", sm: "row" }}
                                        gap={{ base: 4, sm: 6 }}
                                        mb={{ base: 8, md: 10 }}
                                        w="100%"
                                    >
                                        <Box
                                            bg="dark.100" 
                                            px={4} 
                                            py={2} 
                                            borderRadius="full"
                                            order={{ base: 2, sm: 1 }}
                                        >
                                            <Text 
                                                fontSize={{ base: "sm", md: "md" }} 
                                                color="primary.100" 
                                                fontWeight="bold"
                                                textAlign="center"
                                            >
                                                {followers?.toLocaleString() || 0} {followers === 1 ? 'Follower' : 'Followers'}
                                            </Text>
                                        </Box>
                                        
                                        {isAuthenticated && isFollowing !== null && (
                                            <Box order={{ base: 1, sm: 2 }}>
                                                {followLoading ? (
                                                    <Spinner size="md" color="primary.500" />
                                                ) : (
                                                    <IconButton
                                                        variant={isFollowing ? "solid" : "outline"}
                                                        colorScheme={isFollowing ? "red" : "teal"}
                                                        onClick={isFollowing ? unfollowArtist : followArtist}
                                                        isLoading={followLoading}
                                                        icon={<FaThumbsUp />}
                                                        size="lg"
                                                        borderRadius="full"
                                                        aria-label={isFollowing ? 'Unfollow Artist' : 'Follow Artist'}
                                                        _hover={{
                                                            transform: "scale(1.05)",
                                                            boxShadow: "lg"
                                                        }}
                                                        transition="all 0.2s ease"
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Flex>

                                    {/* Genres */}
                                    {artistInfo.genres && artistInfo.genres.length > 0 && (
                                        <Flex 
                                            gap={2} 
                                            wrap="wrap"
                                            justify={{ base: "center", lg: "flex-start" }}
                                            mb={{ base: 6, md: 8 }}
                                        >
                                            {artistInfo.genres.map((genre, index) => (
                                                <Box
                                                    key={index}
                                                    bg="primary.500"
                                                    color="dark.900"
                                                    px={3}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="sm"
                                                    fontWeight="bold"
                                                >
                                                    {genre}
                                                </Box>
                                            ))}
                                        </Flex>
                                    )}

                                    {/* Social Media & Streaming Platforms */}
                                    <Flex 
                                        gap={3} 
                                        wrap="wrap"
                                        justify={{ base: "center", lg: "flex-start" }}
                                        align="center"
                                    >
                                        {/* Social Media */}
                                        {((artistInfo.social_media_handles && artistInfo.social_media_handles.length > 0) || 
                                          (artistInfo.social_media && artistInfo.social_media.length > 0)) && (
                                            <>
                                                {(artistInfo.social_media_handles || artistInfo.social_media || []).map((social, index) => {
                                                    const getSocialIcon = (platform) => {
                                                        switch (platform) {
                                                            case 'instagram': return FaInstagram;
                                                            case 'twitter': return FaTwitter;
                                                            case 'facebook': return FaFacebook;
                                                            case 'youtube': return FaYoutube;
                                                            case 'tiktok': return FaTiktok;
                                                            case 'spotify': return FaSpotify;
                                                            default: return FaInstagram;
                                                        }
                                                    };

                                                    const getSocialColor = (platform) => {
                                                        switch (platform) {
                                                            case 'instagram': return '#E4405F';
                                                            case 'twitter': return '#1DA1F2';
                                                            case 'facebook': return '#1877F2';
                                                            case 'youtube': return '#FF0000';
                                                            case 'tiktok': return '#000000';
                                                            case 'spotify': return '#1ED760';
                                                            default: return 'primary.500';
                                                        }
                                                    };

                                                    return (
                                                        <Tooltip key={index} label={`@${social.handle} on ${social.platform}`} hasArrow>
                                                            <IconButton
                                                                icon={React.createElement(getSocialIcon(social.platform))}
                                                                onClick={() => window.open(social.url, '_blank')}
                                                                variant="solid"
                                                                bg={getSocialColor(social.platform)}
                                                                color="white"
                                                                size="lg"
                                                                borderRadius="full"
                                                                _hover={{ 
                                                                    transform: 'scale(1.1)',
                                                                    boxShadow: '2xl'
                                                                }}
                                                                transition="all 0.2s ease"
                                                                aria-label={`${social.platform} - ${social.handle}`}
                                                            />
                                                        </Tooltip>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {/* Streaming Platforms */}
                                        {artistInfo.artist_id && artistInfo.artist_id.length > 0 && (
                                            <>
                                                {artistInfo.artist_id.map((platform, index) => (
                                                    <Tooltip 
                                                        key={index} 
                                                        label={`Listen on ${platform.platform}`} 
                                                        hasArrow
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                window.open(
                                                                    `https://${platform.platform}.com/artist/${platform.id}`,
                                                                    '_blank'
                                                                )
                                                            }
                                                            variant="solid"
                                                            bg={platform.platform === 'spotify' ? '#1ED760' : '#FA243C'}
                                                            color="white"
                                                            size="lg"
                                                            borderRadius="full"
                                                            icon={platform.platform === 'spotify' ? <FaSpotify /> : <FaApple />}
                                                            aria-label={`${platform.platform} profile`}
                                                            _hover={{
                                                                transform: 'scale(1.1)',
                                                                boxShadow: '2xl'
                                                            }}
                                                            transition="all 0.2s ease"
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </>
                                        )}
                                    </Flex>
                                </Box>
                            </Flex>
                        </Box>
                    </Box>
                </Box>

                {/* Right Column - About Section */}
                <Box flex={{ base: '1', xl: '0.8' }}>
                    <Box
                        bg="secondary.500"
                        borderRadius="2xl"
                        p={{ base: 6, md: 8 }}
                        boxShadow="xl"
                        h="fit-content"
                    >
                        <Heading 
                            fontSize={{ base: "xl", md: "2xl" }}
                            color="dark.900" 
                            mb={4}
                            fontWeight="bold"
                        >
                            About {artistInfo.artist_name}
                        </Heading>
                        {artistInfo.bio ? (
                            <Text 
                                color="primary.100" 
                                fontSize={{ base: "md", md: "lg" }}
                                lineHeight="1.7"
                                whiteSpace="pre-wrap"
                            >
                                {artistInfo.bio}
                            </Text>
                        ) : (
                            <Box
                                bg="dark.100"
                                borderRadius="lg"
                                p={6}
                                border="2px dashed"
                                borderColor="dark.500"
                                textAlign="center"
                            >
                                <Text 
                                    color="primary.500" 
                                    fontSize="md"
                                    fontStyle="italic"
                                >
                                    {artistInfo.artist_name} hasn't added a bio yet.
                                </Text>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Flex>

            {/* Music Catalogue Section */}
            <Box
                bg="secondary.500"
                borderRadius="2xl"
                p={{ base: 6, md: 8 }}
                mb={{ base: 8, md: 10, lg: 12 }}
                boxShadow="xl"
            >
                <Heading 
                    fontSize={{ base: "xl", md: "2xl" }}
                    color="dark.900" 
                    mb={{ base: 6, md: 8 }}
                    fontWeight="bold"
                >
                    Music Catalogue
                </Heading>
                
                {loading ? (
                    <Flex justify="center" p={{ base: 8, md: 12 }}>
                        <Spinner size="xl" color="primary.500" thickness="4px" />
                    </Flex>
                ) : catalogue && catalogue.length > 0 ? (
                    <Box
                        display="flex"
                        overflowX="auto"
                        gap={4}
                        py={3}
                        px={1}
                        mx={-1}
                        css={{
                            scrollSnapType: 'x mandatory',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'primary.500 transparent',
                        }}
                        sx={{
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'primary.500',
                                borderRadius: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        {catalogue.map((track, index) => (
                            <Box
                                as={Link}
                                to={`/track/${track.track_id}`}
                                key={index}
                                flex="0 0 auto"
                                width={{ base: "160px", sm: "180px", md: "200px" }}
                                bg="dark.100"
                                borderRadius="xl"
                                overflow="hidden"
                                scrollSnapAlign="start"
                                transition="all 0.3s ease"
                                _hover={{ 
                                    bg: 'dark.500',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '2xl'
                                }}
                                boxShadow="lg"
                            >
                                <Image
                                    src={track.image_url}
                                    alt={track.track_name}
                                    width="100%"
                                    height={{ base: "160px", sm: "180px", md: "200px" }}
                                    objectFit="cover"
                                />

                                <Box p={4}>
                                    <Text
                                        fontSize={{ base: "sm", md: "md" }}
                                        color="primary.100"
                                        fontWeight="bold"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        mb={1}
                                    >
                                        {track.track_name}
                                    </Text>
                                    <Text
                                        fontSize="sm"
                                        color="primary.500"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                    >
                                        {track.artist_name || 'Unknown Artist'}
                                    </Text>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Flex justify="center" p={{ base: 8, md: 12 }}>
                        <Text 
                            color="primary.500" 
                            fontSize={{ base: "md", md: "lg" }}
                            textAlign="center"
                        >
                            No tracks available yet
                        </Text>
                    </Flex>
                )}
            </Box>
        </Box>
    );
};

export default Artist;